import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { BrowserView } from 'react-device-detect';
import { Link } from 'react-router-dom';
import { mixed, object } from 'yup';

import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import Form, { Arquivo } from 'Component/Form';
import Icone from 'Component/Icone';
import Imagem from 'Component/Imagem';
import Modal from 'Component/Modal';
import RelogioDePonto from 'Interface/RelogiosDePonto';
import VinculoEmpregaticio from 'Interface/VinculosEmpregaticios';
import { PDF } from 'View/constants';
import {
  useAdicionarRelogioDePontoMutation,
  useRecuperarRelogiosDePontoQuery,
} from 'View/Service/Default/RelogiosDePonto';

interface Props {
  dados: VinculoEmpregaticio;
  mes: number;
  ano: number;
}

const regras = object({
  url: mixed()
    .test('tamanho', 'O arquivo é muito grande', (valor) => {
      if (valor && valor.size) {
        return valor.size <= 500000;
      }
      return true;
    })
    .test('tipo', 'Formato não suportado', (valor) => {
      if (valor && valor.type) {
        return PDF.includes(valor.type);
      }
      return true;
    })
    .required('Obrigatório'),
});

const UploadLista = ({ dados, ano, mes }: Props): React.ReactElement => {
  const toast = useToast();
  const [modal, setModal] = useState<boolean>(false);
  const [adicionarRelogio] = useAdicionarRelogioDePontoMutation();

  const {
    data: { relogios_de_ponto: [relogio] } = {
      relogios_de_ponto: [{} as RelogioDePonto],
    },
  } = useRecuperarRelogiosDePontoQuery({
    options: { usuario_id: dados.usuario_id, ano: ano, mes: mes },
  });

  const enviar = async (dadosForm: RelogioDePonto): Promise<void> => {
    try {
      await adicionarRelogio({
        url: dadosForm.url,
        ano,
        mes,
        vinculo_id: dados.id,
      }).unwrap();

      toast({
        conteudo: 'Adicionado com sucesso!',
        tema: 'success',
      });
      setModal(false);
    } catch (erro) {
      toast({
        titulo: 'Erro ao adicionar',
        conteudo: 'Verifique o registro de log para mais detalhes!',
        tema: 'danger',
      });
    }
  };

  return (
    <React.Fragment>
      <div className="d-flex border-bottom align-items-center py-2 px-3">
        <Link to={`/usuarios/editar/${dados.usuario?.id}`}>
          <Imagem
            src={dados.usuario?.imagem}
            alt={dados.usuario?.nome}
            className="rounded-circle object-fit-cover"
            width="80"
            height="80"
          />
        </Link>
        <div className="ms-4 fs-4">
          <Link
            to={`/usuarios/editar/${dados.usuario?.id}`}
            className="text-decoration-none"
          >
            {dados.usuario?.nome}
          </Link>
          <small className="d-block fs-6 text-muted">
            {dados.empresa?.nome_fantasia}
          </small>
        </div>
        <BrowserView className="d-flex flex-grow-1 align-items-center justify-content-end">
          {relogio && (
            <div className="ms-5 fs-5 text-end">
              <div>
                {relogio.assinado ? (
                  <>
                    <img src={relogio.assinatura} height={40} />
                    <small
                      className="d-block text-muted"
                      style={{ fontSize: 10 }}
                    >
                      {relogio.data_da_assinatura
                        ?.data()
                        .toLocaleString(DateTime.DATETIME_SHORT)}
                    </small>
                  </>
                ) : (
                  <Icone nome="x-lg" className="text-danger" />
                )}
              </div>
            </div>
          )}
          <div className="ms-5 fs-5 text-end">
            <div className="fs-4">
              {relogio ? (
                <Icone nome="check-lg" className="text-success" />
              ) : (
                <Icone nome="x-lg" className="text-danger" />
              )}
              <small className="text-muted fs-6 d-block">Enviado</small>
            </div>
          </div>
          <div className="ms-2 fs-5 text-end">
            {relogio ? (
              <Botao
                icone="eye-fill"
                tema="primary"
                className="ms-5"
                onClick={() => window.open(relogio.url)}
              />
            ) : (
              <Botao
                icone="cloud-upload-fill"
                tema="success"
                className="ms-5"
                onClick={() => setModal(true)}
              />
            )}
          </div>
        </BrowserView>
      </div>
      <Modal
        exibir={modal}
        titulo="Upload de arquivo de relógio de ponto"
        aoFechar={setModal}
      >
        <Form onSubmit={enviar} regras={regras}>
          <Arquivo name="url" />
          <React.Fragment>
            <Botao type="submit">Enviar</Botao>{' '}
            <Botao tema="light" onClick={() => setModal(false)}>
              Cancelar
            </Botao>
          </React.Fragment>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default UploadLista;
